import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import type { PaymentKind } from "../types";

interface RejectPaymentDialogProps {
  open: boolean;
  paymentKind: PaymentKind;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
}

const TITLE_KEY: Record<PaymentKind, string> = {
  micropayment: "reject_micropayment",
  stipend: "reject_stipend",
  receipt: "reject_micropayment",
  mileage: "reject_micropayment",
};

export const RejectPaymentDialog = ({
  open,
  paymentKind,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: RejectPaymentDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setError(false);
    }
  }, [open]);

  const handleSubmit = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(true);
      return;
    }
    onConfirm(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(TITLE_KEY[paymentKind])}</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <Label className="sr-only" htmlFor="rejection-reason">
            {t("claimDetail_rejectionReason")}
          </Label>
          <Textarea
            id="rejection-reason"
            aria-invalid={error}
            aria-required="true"
            data-test-id="rejection-reason-textarea"
            className={cn(error && "border-red-500 focus-visible:ring-red-500")}
            placeholder={t("claimDetail_rejectionReason")}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              if (event.target.value.trim()) setError(false);
            }}
            rows={4}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            {t("reject")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
